import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  Shield, 
  ShieldOff,
  Mail,
  AtSign,
  Calendar
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUsers() {
  const { currentTheme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdmin = async (userId) => {
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/admin`, {}, { withCredentials: true });
      toast.success(`Admin status ${response.data.is_admin ? 'granted' : 'revoked'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  return (
    <AdminLayout title="User Management">
      <div 
        className="rounded-2xl overflow-hidden"
        style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
      >
        {loading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton h-16 rounded-xl" />
            ))}
          </div>
        ) : users.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img 
                          src={user.picture} 
                          alt={user.name} 
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${currentTheme.primary}20` }}
                        >
                          <span style={{ color: currentTheme.primary }}>
                            {user.name?.charAt(0)}
                          </span>
                        </div>
                      )}
                      <span className="font-medium">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm opacity-70">
                      <Mail className="w-4 h-4" />
                      {user.email}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm">
                      <AtSign className="w-4 h-4 opacity-50" />
                      {user.username || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm opacity-70">
                      <Calendar className="w-4 h-4" />
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {user.is_admin ? (
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
                      >
                        <Shield className="w-3 h-3" />
                        Admin
                      </span>
                    ) : (
                      <span className="text-sm opacity-50">User</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant={user.is_admin ? 'destructive' : 'outline'}
                      size="sm"
                      onClick={() => toggleAdmin(user.user_id)}
                      data-testid={`toggle-admin-${user.user_id}`}
                    >
                      {user.is_admin ? (
                        <>
                          <ShieldOff className="w-4 h-4 mr-1" />
                          Remove Admin
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4 mr-1" />
                          Make Admin
                        </>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
              No users yet
            </h3>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
